import logger from './logger';

interface UserContext {
  userId: string | null;
  storeId: number | null;
}

interface ErrorInfo {
  type: string;
  message?: string;
  source?: string;
  lineno?: number;
  colno?: number;
  stack?: string;
  errorName?: string;
  userId: string | null;
  storeId: number | null;
  timestamp: string;
  userAgent: string;
  url: string;
  viewport?: {
    width: number;
    height: number;
  };
}

interface PromiseErrorInfo
  extends Omit<
    ErrorInfo,
    'message' | 'source' | 'lineno' | 'colno' | 'stack' | 'errorName' | 'viewport'
  > {
  reason: any;
  reasonType: string;
  reasonMessage: string;
  reasonStack?: string;
  promise: Promise<any>;
}

// グローバルエラーハンドラーを設定
export const setupGlobalErrorHandlers = (): void => {
  // ユーザー情報を取得するヘルパー関数
  const getUserContext = (): UserContext => {
    let userId: string | null = null;
    let storeId: number | null = null;

    try {
      // localStorage.getItem can throw in Safari private browsing mode
      const authSession = localStorage.getItem('authSession');
      if (authSession) {
        const session = JSON.parse(authSession);
        userId = session.userId;
        storeId = session.storeId;
      }
    } catch (e) {
      // localStorage アクセスエラーまたはセッション解析エラーは無視
      // Safari プライベートブラウジングモード等で発生する可能性がある
    }

    return { userId, storeId };
  };

  // window.onerror - 同期的なJavaScriptエラーをキャッチ
  window.onerror = (
    message: string | Event,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error
  ): boolean => {
    const { userId, storeId } = getUserContext();

    const errorInfo: ErrorInfo = {
      type: 'JavaScript Error',
      message: typeof message === 'string' ? message : message.type,
      source,
      lineno,
      colno,
      stack: error?.stack,
      errorName: error?.name,
      userId,
      storeId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };

    logger.error('Global JavaScript error occurred', {
      context: 'global_error_handler',
      ...errorInfo
    });

    // エラー発生をユーザーアクションとしても記録
    logger.userAction('global_error_occurred', {
      errorType: 'JavaScript Error',
      errorMessage: errorInfo.message,
      source,
      userId,
      storeId,
      timestamp: new Date().toISOString()
    });

    // デフォルトのブラウザエラー処理を抑制
    return true;
  };

  // unhandledrejection - Promise rejectionをキャッチ
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const { userId, storeId } = getUserContext();

    const errorInfo: PromiseErrorInfo = {
      type: 'Unhandled Promise Rejection',
      reason: event.reason,
      reasonType: typeof event.reason,
      reasonMessage: event.reason?.message || String(event.reason),
      reasonStack: event.reason?.stack,
      promise: event.promise,
      userId,
      storeId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    logger.error('Unhandled promise rejection occurred', {
      context: 'global_error_handler',
      ...errorInfo
    });

    // エラー発生をユーザーアクションとしても記録
    logger.userAction('promise_rejection_occurred', {
      errorType: 'Promise Rejection',
      errorMessage: errorInfo.reasonMessage,
      userId,
      storeId,
      timestamp: new Date().toISOString()
    });

    // イベントのデフォルト処理を防ぐ
    event.preventDefault();
  });

  // React 18のエラーハンドリング対応
  if (process.env.NODE_ENV === 'development') {
    // 開発環境でのエラー詳細表示
    window.addEventListener('error', (event: ErrorEvent) => {
      if (event.error && event.error.stack) {
        // 開発環境でのみ詳細エラー情報をログに記録
        logger.error('Detailed error information:', {
          error: event.error,
          stack: event.error.stack,
          event: event
        });
      }
    });
  }

  logger.info('Global error handlers have been set up');
};

interface FormattedError {
  name?: string;
  message: string;
  stack?: string;
  timestamp: string;
  [key: string]: any;
}

// エラー情報を構造化して返す
export const formatError = (error: any): FormattedError => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString()
    };
  }

  if (typeof error === 'object' && error !== null) {
    return {
      ...error,
      timestamp: new Date().toISOString()
    };
  }

  return {
    message: String(error),
    timestamp: new Date().toISOString()
  };
};

interface NetworkError {
  message?: string;
  code?: string;
  [key: string]: any;
}

// ネットワークエラーの検出
export const isNetworkError = (error: NetworkError): boolean => {
  return (
    error.message === 'Failed to fetch' ||
    error.message === 'Network request failed' ||
    error.code === 'ECONNREFUSED' ||
    error.code === 'ETIMEDOUT' ||
    !navigator.onLine
  );
};

interface AuthError {
  status?: number;
  response?: { status?: number };
  message?: string;
  [key: string]: any;
}

// 認証エラーの検出
export const isAuthError = (error: AuthError | null | undefined): boolean => {
  if (!error) return false;

  return (
    error.status === 401 ||
    error.response?.status === 401 ||
    (error.message &&
      typeof error.message === 'string' &&
      (error.message.toLowerCase().includes('unauthorized') ||
        error.message.toLowerCase().includes('authentication'))) ||
    false
  );
};

interface ValidationError {
  status?: number;
  response?: { status?: number };
  message?: string;
  detail?: any[];
  [key: string]: any;
}

// バリデーションエラーの検出
export const isValidationError = (error: ValidationError): boolean => {
  return (
    error.status === 422 ||
    error.response?.status === 422 ||
    error.message?.toLowerCase().includes('validation') ||
    Array.isArray(error.detail)
  );
};

// ユーザーフレンドリーなエラーメッセージ生成
export const getUserFriendlyMessage = (error: any): string => {
  if (isNetworkError(error)) {
    return 'ネットワーク接続に問題があります。インターネット接続を確認してください。';
  }

  if (isAuthError(error)) {
    return '認証に失敗しました。再度ログインしてください。';
  }

  if (isValidationError(error)) {
    return '入力内容に誤りがあります。赤色で表示された項目を確認してください。';
  }

  if (error.message) {
    return error.message;
  }

  return '予期しないエラーが発生しました。しばらく時間をおいて再度お試しください。';
};
